"use client";

import { ClientOnly, Flex, Skeleton } from "@chakra-ui/react";
import { ThemeProvider } from "next-themes";
import type { ThemeProviderProps } from "next-themes";
import { useColorMode } from "./use-color-mode";

export function ColorModeProvider(props: ThemeProviderProps) {
  return (
    <ThemeProvider attribute="class" disableTransitionOnChange {...props} />
  );
}

export function ColorModeIcon() {
  const { colorMode } = useColorMode();
  return colorMode === "light" ? "⏾" : "☀️";
}

interface ColorModeButtonProps {
  size?: "xs" | "sm" | "md" | "lg";
}

export function ColorModeButton(_props: ColorModeButtonProps) {
  const { toggleColorMode, colorMode } = useColorMode();
  const isLight = colorMode === "light";

  return (
    <ClientOnly
      fallback={
        <Skeleton
          w={{ base: "44px", md: "52px" }}
          h={{ base: "24px", md: "26px" }}
          borderRadius="full"
        />
      }
    >
      <Flex
        as="button"
        onClick={toggleColorMode}
        position="relative"
        align="center"
        w={{ base: "44px", md: "52px" }}
        h={{ base: "24px", md: "26px" }}
        borderRadius="full"
        bg={
          isLight
            ? "linear-gradient(145deg, rgba(255, 215, 0, 0.4), rgba(255, 180, 0, 0.6))"
            : "linear-gradient(145deg, #4338ca, #7c3aed)"
        }
        border="1px solid"
        borderColor={
          isLight ? "rgba(255, 255, 255, 0.5)" : "rgba(167, 139, 250, 0.5)"
        }
        boxShadow={
          isLight
            ? "inset 0 2px 4px rgba(255, 255, 255, 0.4), inset 0 -2px 4px rgba(0, 0, 0, 0.05), 0 2px 8px rgba(255, 180, 0, 0.4)"
            : "inset 0 1px 3px rgba(255,255,255,0.15), 0 0 12px rgba(124, 58, 237, 0.6), 0 2px 8px rgba(0,0,0,0.5)"
        }
        cursor="pointer"
        style={{ WebkitTapHighlightColor: "transparent" }}
        transition="all 0.4s ease"
      >
        <Flex
          position="absolute"
          left={{
            base: isLight ? "2px" : "calc(100% - 22px)",
            md: isLight ? "2px" : "calc(100% - 24px)",
          }}
          align="center"
          justify="center"
          w={{ base: "20px", md: "22px" }}
          h={{ base: "20px", md: "22px" }}
          borderRadius="full"
          bg={
            isLight ? "rgba(255, 255, 255, 0.95)" : "rgba(255, 255, 255, 0.95)"
          }
          backdropFilter="blur(8px)"
          border="1px solid"
          borderColor={
            isLight ? "rgba(255, 255, 255, 0.9)" : "rgba(167, 139, 250, 0.4)"
          }
          boxShadow={
            isLight
              ? "inset 0 2px 4px rgba(255,255,255,0.8), 0 2px 8px rgba(0,0,0,0.15)"
              : "0 0 8px rgba(167, 139, 250, 0.7), 0 2px 6px rgba(0,0,0,0.4)"
          }
          transition="all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)"
          zIndex={1}
        >
          {isLight ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="65%"
              height="65%"
              viewBox="0 0 24 24"
              fill="rgba(255, 140, 0, 0.8)"
              stroke="rgba(255, 140, 0, 0.8)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="4" fill="rgba(255, 140, 0, 0.8)" />
              <path d="M12 2v2" />
              <path d="M12 20v2" />
              <path d="m4.93 4.93 1.41 1.41" />
              <path d="m17.66 17.66 1.41 1.41" />
              <path d="M2 12h2" />
              <path d="M20 12h2" />
              <path d="m6.34 17.66-1.41 1.41" />
              <path d="m19.07 4.93-1.41 1.41" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="65%"
              height="65%"
              viewBox="0 0 24 24"
              fill="#7c3aed"
              stroke="none"
              style={{ transform: "rotate(-20deg)" }}
            >
              <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
            </svg>
          )}
        </Flex>
      </Flex>
    </ClientOnly>
  );
}
